import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, ShieldX, Loader2, Award, ArrowLeft, ExternalLink,
} from "lucide-react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const COPY = {
  it: {
    eyebrow: "Verifica certificato",
    title_valid: "Certificato AUTENTICO",
    title_invalid: "Certificato non valido",
    desc_valid:
      "Questo certificato è stato emesso ufficialmente da Academia Médicos para Italia ed è verificato crittograficamente.",
    desc_invalid:
      "Non risulta nessun certificato attivo con questo identificatore. Contatta supporto@esamericonoscimento.it per assistenza.",
    issued_to: "Rilasciato a",
    issued_at: "Data di emissione",
    score: "Punteggio globale",
    per_module: "Risultati per modulo",
    cert_id: "ID certificato",
    home: "Vai al sito",
    loading: "Verifica in corso…",
  },
  es: {
    eyebrow: "Verificación de certificado",
    title_valid: "Certificado AUTÉNTICO",
    title_invalid: "Certificado no válido",
    desc_valid:
      "Este certificado fue emitido oficialmente por Academia Médicos para Italia y está verificado criptográficamente.",
    desc_invalid:
      "No existe un certificado activo con este identificador. Contactá supporto@esamericonoscimento.it para soporte.",
    issued_to: "Emitido a",
    issued_at: "Fecha de emisión",
    score: "Puntuación global",
    per_module: "Resultados por módulo",
    cert_id: "ID certificado",
    home: "Ir al sitio",
    loading: "Verificando…",
  },
};

const slotLabel = (s, lang) => (lang === "es" ? s.label_es : s.label_it);

function formatDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "es" ? "es-ES" : "it-IT",
      { day: "2-digit", month: "long", year: "numeric" },
    );
  } catch { return iso.slice(0, 10); }
}

export default function VerifyCertificate() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use the certificate's own language (defaults to IT) when present.
  const lang = data?.language === "es" ? "es" : "it";
  const t = useMemo(() => COPY[lang] || COPY.it, [lang]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API_BASE}/api/exam/verify/${certId}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ is_valid: false, cert_id: certId }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [certId]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FBF8F2]"
           data-testid="verify-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#5A3FC0]" />
          <p className="mt-3 text-sm text-[#5A3FC0]">{t.loading}</p>
        </div>
      </div>
    );
  }

  const valid = !!data?.is_valid;

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-10 bg-[#FBF8F2]"
         data-testid="verify-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  className="w-full max-w-2xl">
        <Link to="/" data-testid="verify-back"
              className="inline-flex items-center gap-1.5 text-sm text-[#5A3FC0] mb-5 hover:underline">
          <ArrowLeft className="h-4 w-4" /> {t.home}
        </Link>

        <div className="rounded-2xl shadow-xl p-6 sm:p-10 text-white"
             data-testid={valid ? "verify-valid" : "verify-invalid"}
             style={{
               background: valid
                 ? "linear-gradient(135deg, #2C1862 0%, #5A3FC0 60%, #B45309 100%)"
                 : "linear-gradient(135deg, #4A0E22 0%, #9E2A48 100%)",
             }}>
          <p className="text-[10px] uppercase tracking-[0.22em] opacity-90 mb-2 inline-flex items-center gap-2">
            {valid
              ? <ShieldCheck className="h-3 w-3" />
              : <ShieldX className="h-3 w-3" />}
            {t.eyebrow}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            {valid ? t.title_valid : t.title_invalid}
          </h1>
          <p className="mt-3 opacity-90 leading-relaxed">
            {valid ? t.desc_valid : t.desc_invalid}
          </p>
          {valid && (
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label={t.issued_to} value={data.user_name} />
              <Field label={t.issued_at} value={formatDate(data.issued_at, lang)} />
              <Field label={t.score}
                     value={`${data.total_correct} / ${data.total_questions}`} />
              <Field label={t.cert_id} value={data.cert_id} mono />
            </div>
          )}
        </div>

        {valid && Array.isArray(data.per_slot) && data.per_slot.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border p-6"
               style={{ borderColor: "#E5DCEF" }}>
            <h2 className="font-serif text-xl text-[#1A1432] mb-3 inline-flex items-center gap-2">
              <Award className="h-4 w-4 text-[#5A3FC0]" /> {t.per_module}
            </h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {data.per_slot.map((s) => (
                <div key={s.slot_id}
                     className="flex items-center justify-between rounded-xl border px-3 py-2"
                     style={{ borderColor: "#E5DCEF", background: "#F6F2FF" }}>
                  <span className="text-sm text-[#1A1432]">
                    {slotLabel(s, lang)}
                  </span>
                  <span className="text-sm font-semibold text-[#2C1862]">
                    {s.correct}/{s.total}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-[#5A3FC0] mt-6 text-center">
          <a href="https://esamericonoscimento.it" target="_blank"
             rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 hover:underline">
            esamericonoscimento.it <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </motion.div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] opacity-75">{label}</p>
      <p className={`text-base font-semibold mt-0.5 ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </p>
    </div>
  );
}
